import React, { useEffect, useState, useRef } from "react";
import api from "./axiosSetup";
import { QRCodeCanvas } from "qrcode.react";
import CustomNavbar from "./CustomNavbar";
import cfg from "../config";
import { Carousel, Badge, Toast, ToastContainer } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const QrHint = () => (
  <div className="card bg-secondary text-white p-3" style={{ borderRadius: 12, fontSize: '0.9rem' }}>
    <p className="fw-semibold mb-1">Wann erscheint ein QR-Code?</p>
    <p className="text-white-50 mb-0">
      Wenn du <strong>heute</strong> angemeldet bist, erscheint dein QR-Code
      automatisch <strong>2 Stunden vor der Abfahrt</strong>. Schau kurz vorher nochmals rein.
    </p>
  </div>
);

const MyQrCode = () => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [token, setToken] = useState(localStorage.getItem("authToken"));

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const fetchQrCodes = async () => {
    setLoading(true);
    try {
      if (!token) return;
      const res = await api.get("/api/member/qr-code", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.qrCodes && res.data.qrCodes.length > 0) {
        setQrCodes(res.data.qrCodes);
        setError(null);
        setErrorType(null);
      } else {
        setQrCodes([]);
        setError(res.data.message || "Keine Buchungen gefunden.");
        setErrorType("warning");
      }
    } catch (err) {
      console.error("Fehler beim Laden der QR-Codes:", err);
      setQrCodes([]);
      setError("Fehler beim Laden der QR-Codes.");
      setErrorType("error");
    } finally {
      setLoading(false);
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case "bus": return { text: "Hinfahrt", color: "bg-primary" };
      case "assembly": return { text: "Aufstellungsplatz", color: "bg-danger" };
      default: return { text: "Unbekannt", color: "bg-primary" };
    }
  };

  const showToast = (content, type = "success") => {
    const id = Date.now() + Math.random(); // eindeutige ID
    setToasts(prev => [...prev, { id, content, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  // WebSocket initialisieren
  useEffect(() => {
    if (!token) return;

    const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
    const wsUrl = `${wsProtocol}://${window.location.host}`;

    const connectWebSocket = () => {
      console.log("WebSocket versuche zu verbinden...");
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        // Token als erste Nachricht senden, nicht in der URL
        wsRef.current.send(JSON.stringify({ type: 'auth', token }));
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'auth_ok') {
          console.log("WebSocket authentifiziert.");
          return;
        }
        console.log("WebSocket Event erhalten:", data);
        const phaseText = getPhaseLabel(data.phase)?.text || data.phase;
        showToast(
          <div>Buchung für <strong>{phaseText}</strong> bestätigt!</div>,
          "success"
        );
        setTimeout(fetchQrCodes, 3000);
      };

      wsRef.current.onerror = (err) => {
        console.error("WebSocket Fehler:", err);
      };

      wsRef.current.onclose = (event) => {
        if (event.code === 4001) {
          // Token abgelaufen – kein Reconnect, zur Login-Seite
          localStorage.removeItem("authToken");
          window.location.href = "/login";
          return;
        }
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };
    };

    connectWebSocket();

    return () => {
      wsRef.current?.close();
      clearTimeout(reconnectTimeoutRef.current);
    };
  }, [token]);

  // QR-Codes laden, sobald Token verfügbar ist
  useEffect(() => {
    fetchQrCodes();
  }, [token]);

  return (
    <div>
      <CustomNavbar />
      <div className="container mt-5 d-flex flex-column align-items-center" style={{ maxWidth: "420px" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Lädt...</span>
            </div>
          </div>
        ) : error ? (
          <div className="w-100">
            <div className={`alert alert-${errorType || "warning"} text-center mb-3`}>{error}</div>
            <QrHint />
          </div>
        ) : qrCodes.length === 0 ? (
          <div className="w-100">
            <div className="alert alert-warning text-center mb-3">Aktuell kein QR-Code verfügbar.</div>
            <QrHint />
          </div>
        ) : (
          <Carousel interval={null} indicators={qrCodes.length > 1} controls={qrCodes.length > 1} touch className="w-100">
            {qrCodes.map((item, index) => {
              const phaseLabel = getPhaseLabel(item.phase);
              return (
                <Carousel.Item key={item.bookingID}>
                  <div className="card shadow-lg p-4 text-center rounded-4" style={{ backgroundColor: "#f8f9fa", border: "2px solid #007bff" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center gap-2">
                        {item.profilePicture && (
                          <img
                            src={item.profilePicture}
                            alt=""
                            style={{
                              width: 44, height: 44, borderRadius: '50%',
                              objectFit: 'cover',
                              border: '2px solid #007bff',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <h4 className="fw-semibold text-primary mb-0">{item.memberName}</h4>
                      </div>
                      <Badge bg="secondary">{index + 1} / {qrCodes.length}</Badge>
                    </div>
                    <p className="mb-1 text-muted"><strong>{item.typ}</strong> – {cfg.costumeTerm} #{item.haesNumber}</p>
                    <p className="mb-1"><strong>{cfg.eventTerm}:</strong> {item.moveName}</p>
                    <span className={`badge ${phaseLabel.color} mb-3`} style={{ fontSize: "1rem", padding: "0.5rem 1rem", borderRadius: "12px" }}>
                      {phaseLabel.text}
                    </span>
                    <div className="d-flex justify-content-center mb-3">
                      <QRCodeCanvas value={JSON.stringify(item.qrData)} size={220} />
                    </div>
                  </div>
                </Carousel.Item>
              );
            })}
          </Carousel>
        )}

        <ToastContainer className="p-3" position="top-center">
          {toasts.map(toast => (
            <Toast key={toast.id} bg={toast.type} className="mb-2">
              <Toast.Body className="text-white text-center">{toast.content}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>
      </div>
    </div>
  );
};

export default MyQrCode;
